/**
 * Guest Return Request — Devolución sin iniciar sesión.
 *
 * Flujo:
 * 1) Busca el pedido via proxy (/api/guest-order-lookup)
 * 2) Inyecta #rr-config y #rr-order-data
 * 3) Abre el mismo modal del tema (ReturnRequest) para que conserve CSS/animaciones
 *
 * Nota: el bloqueo por plazo (15 días) lo gestiona ReturnRequest.onTypeSelected()
 * leyendo orderData.withdrawalEligible / withdrawalBlockReason.
 */
(function () {
  'use strict';

  let PROXY_URL = '';
  let guestFlowActive = false;
  let closeWatcherSetUp = false;

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);

  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  function getConfig() {
    const el = $('#guest-rr-config');
    if (el) {
      try {
        const c = JSON.parse(el.textContent);
        if (c.proxyUrl) PROXY_URL = String(c.proxyUrl).trim();
      } catch (e) {
        // ignore
      }
    }

    if (!PROXY_URL) {
      const meta = document.querySelector('meta[name="return-proxy-url"]');
      if (meta && meta.getAttribute('content')) PROXY_URL = meta.getAttribute('content').trim();
    }
  }

  function showError(msg) {
    const el = $('#guest-return-error');
    if (!el) return;
    el.textContent = msg || '';
    el.style.display = msg ? 'block' : 'none';
  }

  function setLoading(loading) {
    const btn = $('#guest-lookup-btn');
    const spinner = $('#guest-lookup-spinner');
    const btnText = $('.guest-return__btn-text', btn);
    if (btn) {
      btn.disabled = loading;
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
    if (spinner) spinner.style.display = loading ? 'inline-flex' : 'none';
    if (btnText) btnText.style.display = loading ? 'none' : 'inline';
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function loadGuestI18n() {
    const el = document.getElementById('rr-i18n-json');
    if (!el) return {};
    try {
      const data = JSON.parse(el.textContent);
      return data.strings || {};
    } catch (e) {
      return {};
    }
  }

  const GUEST_I18N = loadGuestI18n();

  function guestStr(key, fallback) {
    return GUEST_I18N[key] || fallback;
  }

  function getLocaleLabels() {
    const host = (window.location.hostname || '').toLowerCase();
    if (host.includes('newgarden.pt') || host.includes('newgardenshop.pt')) {
      return { qtyLabel: 'Quantidade', unitsAbbr: 'un.' };
    }
    if (host.includes('newgardenshopping.nl') || host.includes('newgarden.nl')) {
      return { qtyLabel: 'Aantal', unitsAbbr: 'st.' };
    }
    return { qtyLabel: 'Cantidad', unitsAbbr: guestStr('guestUnitsAbbr', 'uds.') };
  }

  function productNoReturnDisclaimer() {
    const el = document.getElementById('rr-i18n-json');
    if (el) {
      try {
        const i18n = JSON.parse(el.textContent);
        if (i18n.productNoReturnDisclaimer) return i18n.productNoReturnDisclaimer;
      } catch (e) { /* ignore */ }
    }
    const host = (window.location.hostname || '').toLowerCase();
    if (host.includes('newgardenshop.pt') || host.includes('newgarden.pt')) return 'Este artigo não admite devolução.';
    if (host.includes('newgardenshopping.nl') || host.includes('newgarden.nl')) return 'Dit artikel kan niet worden geretourneerd.';
    return 'Este artículo no admite devolución.';
  }

  function isNoReturnItem(item) {
    return item.sinDevolucion === true || item.sinDevolucion === 'true' || item.sinDevolucion === 1;
  }

  function buildProductCardsHTML(lineItems) {
    const { unitsAbbr } = getLocaleLabels();
    return lineItems.map(function (item) {
      const id = item.id;
      const maxQty = Math.max(1, parseInt(item.quantity, 10) || 1);
      const title = escapeHtml(item.productTitle || '');
      const variantTitle = escapeHtml(item.variantTitle || '');
      const priceFormatted = escapeHtml(item.priceFormatted || '');
      const host = (window.location.hostname || '').toLowerCase();
      const isNl = host.includes('newgardenshopping.nl');
      const pickupFlag = isNl && item.disponiblePickupNl != null
        ? item.disponiblePickupNl
        : item.disponiblePickup;
      const disponiblePickup = pickupFlag === true || pickupFlag === 'true' || pickupFlag === 1;
      const disponibleAttr = ' data-disponible-pickup="' + (disponiblePickup ? 'true' : 'false') + '"';
      const img = item.image
        ? '<img src="' + escapeHtml(item.image) + '" width="70" height="70" alt="" loading="lazy">'
        : '<div class="rr-product-placeholder" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="M21 15l-5-5L5 21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></div>';

      if (isNoReturnItem(item)) {
        const noReturnNote = escapeHtml(productNoReturnDisclaimer());
        return (
          '<div class="rr-product-card rr-product-card--no-return" data-line-item-id="' + escapeHtml(String(id)) + '" data-max-qty="' + maxQty + '" data-no-return="true"' + disponibleAttr + '>' +
            '<div class="rr-product-checkbox" aria-hidden="true">' +
              '<input type="checkbox" class="rr-checkbox" disabled aria-disabled="true" tabindex="-1">' +
              '<span class="rr-checkmark rr-checkmark--disabled"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M4 4l6 6M10 4l-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></span>' +
            '</div>' +
            '<div class="rr-product-image">' + img + '</div>' +
            '<div class="rr-product-info">' +
              '<span class="rr-product-name">' + title + '</span>' +
              (variantTitle ? '<span class="rr-product-variant">' + variantTitle + '</span>' : '') +
              '<span class="rr-product-price">' + maxQty + ' ' + unitsAbbr + ' · ' + priceFormatted + '</span>' +
              '<p class="rr-product-no-return-note">' + noReturnNote + '</p>' +
            '</div>' +
          '</div>'
        );
      }

      return (
        '<label class="rr-product-card" data-line-item-id="' + escapeHtml(String(id)) + '" data-max-qty="' + maxQty + '"' + disponibleAttr + '>' +
        '<div class="rr-product-checkbox">' +
        '<input type="checkbox" class="rr-checkbox" name="return_items[]" value="' + escapeHtml(String(id)) + '">' +
        '<span class="rr-checkmark"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11.5 3.5L5.5 10L2.5 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
        '</div>' +
        '<div class="rr-product-image">' + img + '</div>' +
        '<div class="rr-product-info">' +
        '<span class="rr-product-name">' + title + '</span>' +
        (variantTitle ? '<span class="rr-product-variant">' + variantTitle + '</span>' : '') +
        '<span class="rr-product-price">' + maxQty + ' ' + unitsAbbr + ' · ' + priceFormatted + '</span>' +
        '</div>' +
        (maxQty > 1
          ? '<div class="rr-stepper">' +
            '<button type="button" class="rr-stepper__btn rr-stepper__btn--minus" onclick="ReturnRequest.adjustQty(this, -1)" aria-label="' + escapeHtml(guestStr('qtyDecreaseAria', 'Reducir cantidad')) + '" disabled>' +
            '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6h7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
            '</button>' +
            '<span class="rr-stepper__display">1</span>' +
            '<input type="hidden" class="rr-qty-input" value="1" data-max="' + maxQty + '" min="1" max="' + maxQty + '">' +
            '<button type="button" class="rr-stepper__btn rr-stepper__btn--plus" onclick="ReturnRequest.adjustQty(this, 1)" aria-label="' + escapeHtml(guestStr('qtyIncreaseAria', 'Aumentar cantidad')) + '">' +
            '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2.5v7M2.5 6h7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
            '</button>' +
            '</div>'
          : '<input type="hidden" class="rr-qty-input" value="1" data-max="1" min="1" max="1">') +
        '</label>'
      );
    }).join('');
  }

  function lookupOrder() {
    getConfig();
    const orderInput = $('#guest-order-number');
    const emailInput = $('#guest-email');
    const orderNumber = (orderInput && orderInput.value) ? orderInput.value.trim().replace(/^#+/, '') : '';
    const email = (emailInput && emailInput.value) ? emailInput.value.trim() : '';

    if (!orderNumber || !email) {
      showError(guestStr('guestErrorMissingFields', 'Introduce el número de pedido y el correo electrónico.'));
      return;
    }
    const shop = (typeof window.Shopify !== 'undefined' && window.Shopify.shop)
      ? window.Shopify.shop
      : (window.location.hostname || '');
    if (!shop) {
      showError(guestStr('guestErrorNoShop', 'No se pudo detectar la tienda.'));
      return;
    }
    if (!PROXY_URL) {
      showError(guestStr('guestErrorNoProxy', 'Servicio de devoluciones no configurado. Contacta con la tienda.'));
      return;
    }

    showError('');
    setLoading(true);

    const url = PROXY_URL.replace(/\/$/, '') + '/api/guest-order-lookup';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ orderNumber, email, shop })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            // El proxy responde con texto en español: se traduce por estado HTTP con los textos de la tienda.
            if (res.status === 404) throw new Error(guestStr('guestErrorNotFound', data.error));
            if (res.status === 400) throw new Error(guestStr('guestErrorInvalidOrder', data.error));
            throw new Error(guestStr('guestErrorLookupFailed', data.error || 'Error al buscar el pedido.'));
          }
          return data;
        });
      })
      .then(function (data) {
        if (data.alreadyHasReturn) {
          showError(
            guestStr('guestErrorAlreadyHasReturn', 'Este pedido ya tiene una solicitud de devolución')
            + (data.returnName ? ' (' + data.returnName + ')' : '')
            + guestStr('guestErrorAlreadyHasReturnSuffix', '. Si necesitas ayuda, contacta con atención al cliente.')
          );
          return;
        }
        const hasReturnable = data.lineItems && data.lineItems.some(function (item) { return !isNoReturnItem(item); });
        if (data.noReturnableItems || !data.lineItems || data.lineItems.length === 0 || !hasReturnable) {
          showError(guestStr('guestErrorNoReturnable', 'No hay productos que se puedan devolver en este pedido.'));
          return;
        }
        renderReturnFlow(data);
      })
      .catch(function (err) {
        const msg = err && err.message ? err.message : '';
        if (!PROXY_URL) {
          showError(guestStr('guestErrorProxyConfig', 'Servicio de devoluciones no configurado. En el tema, configura la URL del proxy.'));
        } else if (msg === 'Failed to fetch') {
          showError(
            guestStr('guestErrorFetchFailed', 'No se pudo conectar con el proxy')
            + ' (' + PROXY_URL + '). '
            + guestStr('guestErrorFetchFailedSuffix', 'Revisa en Vercel: ALLOWED_ORIGINS debe incluir')
            + ' ' + window.location.origin + ' '
            + guestStr('guestErrorFetchFailedSuffix2', 'y credenciales de la tienda.')
          );
        } else {
          showError(msg || guestStr('guestErrorLoadFailed', 'Error al cargar el pedido. Inténtalo de nuevo.'));
        }
      })
      .finally(function () {
        setLoading(false);
      });
  }

  function renderReturnFlow(data) {
    const form = $('#guest-return-lookup-form');
    const container = $('#guest-return-container');
    const wrapper = $('#guest-return-modal-wrapper');
    const listEl = $('#rr-guest-products-list');
    const nameEl = $('#rr-guest-order-name');

    if (!wrapper || !data.orderId || !data.lineItems || data.lineItems.length === 0) return;

    if (form) form.style.display = 'none';
    if (container) container.style.display = 'block';
    if (wrapper) wrapper.style.display = 'block';

    if (nameEl) nameEl.textContent = data.orderName || '';

    const orderData = {
      orderId: data.orderId,
      orderName: data.orderName,
      orderGid: data.orderGid || data.orderId,
      pricing: data.pricing || null,
      lineItems: data.lineItems,
      shippingAddress: data.shippingAddress || null,
      // Para bloqueo por plazo de desistimiento
      withdrawalEligible: data.withdrawalEligible,
      withdrawalBlockReason: data.withdrawalBlockReason,
      withdrawalDaysLimit: data.withdrawalDaysLimit,
      lastDeliveredAt: data.lastDeliveredAt,
      deliveryCarrier: data.deliveryCarrier || null,
      daysSinceDelivery: data.daysSinceDelivery
    };

    let configEl = $('#rr-config');
    let dataEl = $('#rr-order-data');
    if (!configEl) {
      configEl = document.createElement('script');
      configEl.type = 'application/json';
      configEl.id = 'rr-config';
      document.body.appendChild(configEl);
    }
    if (!dataEl) {
      dataEl = document.createElement('script');
      dataEl.type = 'application/json';
      dataEl.id = 'rr-order-data';
      document.body.appendChild(dataEl);
    }

    configEl.textContent = JSON.stringify({ proxyUrl: PROXY_URL });
    dataEl.textContent = JSON.stringify(orderData);

    if (listEl) listEl.innerHTML = buildProductCardsHTML(data.lineItems);

    if (typeof ReturnRequest !== 'undefined' && ReturnRequest.reloadOrderData) {
      ReturnRequest.reloadOrderData();
    }
    guestFlowActive = true;
    if (typeof ReturnRequest !== 'undefined' && ReturnRequest.open) {
      ReturnRequest.open();
    }
  }

  function resetToLookup() {
    if (!guestFlowActive) return;
    guestFlowActive = false;
    const form = $('#guest-return-lookup-form');
    const container = $('#guest-return-container');
    const wrapper = $('#guest-return-modal-wrapper');
    if (form) form.style.display = '';
    if (container) container.style.display = 'none';
    if (wrapper) wrapper.style.display = 'none';
    showError('');
  }

  function watchModalClose() {
    if (closeWatcherSetUp) return;
    const overlayEl = document.getElementById('ReturnRequestOverlay');
    if (!overlayEl) return;
    closeWatcherSetUp = true;
    let wasOpen = false;

    const obs = new MutationObserver(function () {
      const hidden = overlayEl.getAttribute('aria-hidden') === 'true';
      if (wasOpen && hidden) {
        setTimeout(resetToLookup, 520);
      }
      wasOpen = !hidden;
    });

    obs.observe(overlayEl, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  function bindEvents() {
    const form = $('#guest-return-lookup-form');
    if (!form) return;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      lookupOrder();
    });
  }

  function init() {
    getConfig();
    bindEvents();
    watchModalClose();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

